import { AdManagementService, type BatchAdResponse, type GeneratedAdResponse } from '../client'
import { DEFAULT_ADSET_NAME, DEFAULT_CAMPAIGN_NAME, type AdCreative, type PromotionForm, type StepStatus } from './adPromotion'

export type PublishStep = 'campaign' | 'adset' | 'ad'
export type PublishStatuses = Record<PublishStep, StepStatus>

export const IDLE_STATUSES: PublishStatuses = { campaign: 'pending', adset: 'pending', ad: 'pending' }

type Args = {
  brandId: number
  ads: GeneratedAdResponse[]
  creatives: AdCreative[]
  form: PromotionForm
  newCampaign: boolean
  onProgress: (statuses: PublishStatuses) => void
}

/** Campaign, then ad set, then the ads — reusing whichever of the first two already exist.
 * Reports each step as it goes and marks the one that threw before rethrowing. */
export async function publishAds({ brandId, ads, creatives, form, newCampaign, onProgress }: Args) {
  const newAdset = newCampaign || form.adsetMode === 'new'
  const campaignName = newCampaign ? form.campaignName || DEFAULT_CAMPAIGN_NAME : form.existingCampaignName
  const adsetName = newAdset ? form.adsetName || DEFAULT_ADSET_NAME : form.existingAdsetName

  const statuses: PublishStatuses = { ...IDLE_STATUSES }
  let step: PublishStep = 'campaign'

  function advance(done: PublishStep, next?: PublishStep) {
    statuses[done] = 'done'
    if (next) {
      statuses[next] = 'in_progress'
      step = next
    }
    onProgress({ ...statuses })
  }

  statuses.campaign = 'in_progress'
  onProgress({ ...statuses })

  try {
    const campaignId = newCampaign
      ? (
          await AdManagementService.createCampaign(brandId, {
            campaign_name: campaignName,
            objective: form.objective,
            buying_type: 'AUCTION',
          })
        ).campaign_id
      : form.existingCampaignId

    advance('campaign', 'adset')

    const adsetId = newAdset
      ? (
          await AdManagementService.createAdset(brandId, {
            campaign_id: campaignId,
            objective: form.objective,
            adset_name: adsetName,
            budget_type: form.budgetType,
            ...(form.budgetType === 'DAILY'
              ? { daily_budget: String(form.budget) }
              : { lifetime_budget: String(form.budget) }),
            targeting_locations: form.targetingLocations.length
              ? form.targetingLocations.map(({ key, type }) => ({ key, type }))
              : [{ key: 'US', type: 'country' }],
            billing_event: form.billingEvent,
            bid_strategy: 'LOWEST_COST_WITHOUT_CAP',
            optimization_goal: form.optimizationGoal,
            start_time: form.startTime || undefined,
            end_time: form.endTime || undefined,
          })
        ).adset_id
      : form.existingAdsetId

    advance('adset', 'ad')

    const result: BatchAdResponse = await AdManagementService.createBatchAd(brandId, {
      campaign_id: campaignId,
      adset_id: adsetId,
      campaign_name: campaignName || undefined,
      adset_name: adsetName || undefined,
      ads: ads.map((ad, index) => ({
        image_url: ad.image_url ?? null,
        ad_name: creatives[index].adName || `Adlovin Generated Ad ${index + 1}`,
        primary_text: creatives[index].primaryText || undefined,
        headline: creatives[index].headline || undefined,
        description: creatives[index].description || undefined,
        cta_type: creatives[index].ctaType,
        cta_link: creatives[index].ctaLink || undefined,
      })),
    })

    advance('ad')
    return result
  } catch (err) {
    statuses[step] = 'error'
    onProgress({ ...statuses })
    throw err
  }
}
